/**
 * SoleadLab 2.0 — Date Filter Toolbar
 *
 * Floating toolbar over the map for managing capture dates:
 * - Select the active capture date (new items get tagged with it)
 * - Filter map visibility by date (checkbox per date with item counts)
 * - Add new capture dates via modal
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useProjectStore } from '../../stores';
import { captureDay } from '../../utils/captureDate';
import { observationDays } from '../../utils/observations';
import './DateFilterToolbar.css';

/** Format YYYY-MM-DD → "Dec 20, 2025" */
function formatDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DateFilterToolbar() {
  const selectedDate = useProjectStore((s) => s.selectedDate);
  const availableDates = useProjectStore((s) => s.availableDates);
  const visibleDates = useProjectStore((s) => s.visibleDates);
  const setSelectedDate = useProjectStore((s) => s.setSelectedDate);
  const addCaptureDate = useProjectStore((s) => s.addCaptureDate);
  const toggleDateVisibility = useProjectStore((s) => s.toggleDateVisibility);
  const setAllDatesVisible = useProjectStore((s) => s.setAllDatesVisible);
  const setNoDatesVisible = useProjectStore((s) => s.setNoDatesVisible);

  // Data arrays for item counts
  const indications = useProjectStore((s) => s.data.indications);
  const orthophotos = useProjectStore((s) => s.data.orthophotos);
  const flightPaths = useProjectStore((s) => s.data.flightPaths);
  const geoPhotos = useProjectStore((s) => s.data.geoPhotos);

  // Local UI state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [newDateValue, setNewDateValue] = useState('');

  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        filterBtnRef.current && !filterBtnRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  // Item counts per date (overlays excluded — not date-filtered)
  const dateCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const date of availableDates) {
      let count = 0;
      // Observation-aware: a persistent fault counts on every date it was observed.
      count += indications.filter((i) => observationDays(i).includes(date)).length;
      count += orthophotos.filter((o) => captureDay(o.captureDate) === date).length;
      count += flightPaths.filter((f) => captureDay(f.captureDate) === date).length;
      count += geoPhotos.filter((p) => captureDay(p.captureDate) === date).length;
      counts.set(date, count);
    }
    return counts;
  }, [availableDates, indications, orthophotos, flightPaths, geoPhotos]);

  // ── Year → date grouping (inspection selector) ──
  // The dock runs frequent missions, so dates pile up. Group them by YEAR so the
  // filter reads as inspection campaigns: pick a whole year or drill to a date.
  const datesByYear = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const d of availableDates) {
      const y = d.slice(0, 4);
      const arr = m.get(y) ?? [];
      arr.push(d);
      m.set(y, arr);
    }
    return [...m.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))                 // newest year first
      .map(([year, dates]) => ({ year, dates: [...dates].sort((a, b) => b.localeCompare(a)) }));
  }, [availableDates]);

  const yearCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const { year, dates } of datesByYear) {
      m.set(year, dates.reduce((s, d) => s + (dateCounts.get(d) || 0), 0));
    }
    return m;
  }, [datesByYear, dateCounts]);

  // Expanded year groups. Default-expand the newest year + the active date's year.
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
  const didInitExpandRef = useRef(false);
  useEffect(() => {
    if (didInitExpandRef.current || datesByYear.length === 0) return;
    didInitExpandRef.current = true;
    const init = new Set<string>();
    if (datesByYear[0]) init.add(datesByYear[0].year);
    if (selectedDate) init.add(selectedDate.slice(0, 4));
    setExpandedYears(init);
  }, [datesByYear, selectedDate]);

  const toggleYearExpand = (year: string) =>
    setExpandedYears((s) => {
      const n = new Set(s);
      if (n.has(year)) n.delete(year); else n.add(year);
      return n;
    });

  const yearVisibility = (dates: string[]): 'all' | 'none' | 'some' => {
    const vis = dates.filter((d) => visibleDates.includes(d)).length;
    if (vis === 0) return 'none';
    return vis === dates.length ? 'all' : 'some';
  };

  // Year checkbox: if the whole year is visible → hide it; else show all its dates.
  const toggleYear = (dates: string[]) => {
    const show = yearVisibility(dates) !== 'all';
    for (const d of dates) {
      if (visibleDates.includes(d) !== show) toggleDateVisibility(d);
    }
  };

  // Filter button label
  const filterLabel = useMemo(() => {
    if (availableDates.length === 0) return 'All dates';
    if (visibleDates.length === availableDates.length) return 'All dates';
    if (visibleDates.length === 0) return 'None';
    return `${visibleDates.length}/${availableDates.length} dates`;
  }, [visibleDates, availableDates]);

  const handleAddDate = () => {
    if (!newDateValue) return;
    addCaptureDate(newDateValue);
    setNewDateValue('');
    setShowAddModal(false);
  };

  const openAddModal = () => {
    setNewDateValue(new Date().toISOString().split('T')[0]);
    setShowAddModal(true);
  };

  return (
    <div className="date-toolbar">
      {/* Left: Active capture date */}
      <div className="date-toolbar__section">
        <span className="date-toolbar__label">Capture Date:</span>
        <select
          className="date-toolbar__select"
          value={selectedDate || ''}
          onChange={(e) => setSelectedDate(e.target.value || null)}
        >
          <option value="">— none —</option>
          {datesByYear.map(({ year, dates }) => (
            <optgroup key={year} label={year}>
              {dates.map((date) => (
                <option key={date} value={date}>{formatDateLabel(date)}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <button className="date-toolbar__add-btn" onClick={openAddModal} title="Add capture date">
          +
        </button>
      </div>

      <div className="date-toolbar__divider" />

      {/* Right: Visibility filter */}
      <div className="date-toolbar__section">
        <span className="date-toolbar__label">Show:</span>
        <button
          ref={filterBtnRef}
          className="date-toolbar__filter-btn"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          {filterLabel}
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 1l4 4 4-4" />
          </svg>
        </button>

        {showDropdown && (
          <div ref={dropdownRef} className="date-filter-dropdown">
            <div className="date-filter-dropdown__header">
              <button onClick={() => { setAllDatesVisible(); }}>All</button>
              <button onClick={() => { setNoDatesVisible(); }}>None</button>
            </div>
            <div className="date-filter-dropdown__list">
              {availableDates.length === 0 && (
                <p className="date-filter-dropdown__empty">No dates added yet</p>
              )}
              {datesByYear.map(({ year, dates }) => {
                const yv = yearVisibility(dates);
                const expanded = expandedYears.has(year);
                return (
                  <div key={year} className="date-filter-year">
                    <div className="date-filter-year__header">
                      <button
                        type="button"
                        className="date-filter-year__chevron"
                        onClick={() => toggleYearExpand(year)}
                        aria-label={expanded ? 'Collapse year' : 'Expand year'}
                      >
                        {expanded ? '▾' : '▸'}
                      </button>
                      <input
                        type="checkbox"
                        checked={yv === 'all'}
                        ref={(el) => { if (el) el.indeterminate = yv === 'some'; }}
                        onChange={() => toggleYear(dates)}
                      />
                      <span className="date-filter-year__label" onClick={() => toggleYearExpand(year)}>
                        {year} <span className="date-filter-year__sub">· {dates.length} date{dates.length > 1 ? 's' : ''}</span>
                      </span>
                      <span className="date-filter-year__count">{yearCounts.get(year) || 0}</span>
                    </div>
                    {expanded && (
                      <div className="date-filter-year__dates">
                        {dates.map((date) => (
                          <label key={date} className="date-filter-dropdown__item">
                            <input
                              type="checkbox"
                              checked={visibleDates.includes(date)}
                              onChange={() => toggleDateVisibility(date)}
                            />
                            <span className="date-filter-dropdown__date">{formatDateLabel(date)}</span>
                            <span className="date-filter-dropdown__count">{dateCounts.get(date) || 0}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add Date Modal */}
      {showAddModal && (
        <>
          <div className="date-add-backdrop" onClick={() => setShowAddModal(false)} />
          <div className="date-add-modal">
            <div className="date-add-modal__title">Add Capture Date</div>
            <input
              type="date"
              className="date-add-modal__input"
              value={newDateValue}
              onChange={(e) => setNewDateValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddDate(); }}
            />
            <div className="date-add-modal__actions">
              <button className="date-add-modal__btn date-add-modal__btn--cancel" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="date-add-modal__btn date-add-modal__btn--add" onClick={handleAddDate} disabled={!newDateValue}>
                Add
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
